import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Receipt, 
  X,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppImage from '../../../components/AppImage';

const RecordPaymentModal = ({ 
  isOpen, 
  onClose, 
  invoice, 
  customerOptions,
  onRecordPayment 
}) => {
  const [formData, setFormData] = useState({
    amount_received: '',
    payment_method: 'bank_transfer',
    payment_date: new Date()?.toISOString()?.split('T')?.[0],
    reference_number: '',
    bank_name: '',
    account_reference: '',
    description: '',
    notes: '',
    status: 'pending'
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes or invoice changes
  React.useEffect(() => {
    if (isOpen && invoice) {
      // Calculate remaining balance
      const totalAmount = parseFloat(invoice?.total_amount || 0);
      const amountPaid = parseFloat(invoice?.amount_paid || 0);
      const remainingBalance = totalAmount - amountPaid;

      setFormData({
        amount_received: remainingBalance > 0 ? remainingBalance?.toFixed(2) : '',
        payment_method: 'bank_transfer',
        payment_date: new Date()?.toISOString()?.split('T')?.[0],
        reference_number: '',
        bank_name: '',
        account_reference: '',
        description: `Payment for Invoice ${invoice?.invoice_number}`,
        notes: '',
        status: 'pending'
      });
      setErrors({});
    }
  }, [isOpen, invoice]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.amount_received || parseFloat(formData?.amount_received) <= 0) {
      newErrors.amount_received = 'Amount must be greater than 0';
    }

    if (!formData?.payment_method) {
      newErrors.payment_method = 'Payment method is required';
    }

    if (!formData?.payment_date) {
      newErrors.payment_date = 'Payment date is required';
    }

    // Validate customer_id is available from invoice
    if (!invoice?.customer_id) {
      newErrors.customer_id = 'Invoice must have a valid customer. Please ensure the invoice has a customer assigned.';
    }

    // Validate amount doesn't exceed remaining balance
    const totalAmount = parseFloat(invoice?.total_amount || 0);
    const amountPaid = parseFloat(invoice?.amount_paid || 0);
    const remainingBalance = totalAmount - amountPaid;
    const paymentAmount = parseFloat(formData?.amount_received || 0);

    if (paymentAmount > remainingBalance) {
      newErrors.amount_received = `Payment amount cannot exceed remaining balance of $${remainingBalance?.toFixed(2)}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Additional safety check for customer_id
    if (!invoice?.customer_id) {
      setErrors({
        submit: 'Cannot record payment: Invoice is missing customer information. Please ensure the invoice has a valid customer assigned.'
      });
      return;
    }

    setLoading(true);
    try {
      await onRecordPayment({
        ...formData,
        customer_id: invoice?.customer_id,
        reference_invoice_id: invoice?.id,
        amount_received: parseFloat(formData?.amount_received)
      });
    } catch (error) {
      console.error('Error recording payment:', error);
      // Enhanced error handling for customer_id constraint violation
      if (error?.message?.includes('customer_id') || error?.code === '23502') {
        setErrors({
          submit: 'Payment creation failed: Missing customer information. The invoice must have a valid customer assigned before recording payments.'
        });
      } else {
        setErrors({
          submit: 'Failed to record payment. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !invoice) return null;

  // Early return if invoice is missing customer data with error display
  if (invoice && !invoice?.customer_id) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

          {/* Error Modal */}
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Invalid Invoice</h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                This invoice is missing customer information and cannot have payments recorded. 
                Please ensure the invoice has a valid customer assigned before attempting to record payments.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate payment summary
  const totalAmount = parseFloat(invoice?.total_amount || 0);
  const amountPaid = parseFloat(invoice?.amount_paid || 0);
  const remainingBalance = totalAmount - amountPaid;
  const paymentAmount = parseFloat(formData?.amount_received || 0);
  const newBalance = remainingBalance - paymentAmount;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Record Payment</h3>
                <p className="text-sm text-gray-500">Invoice {invoice?.invoice_number}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Payment Reference Image */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AppImage 
                  src="/assets/images/image-1762242597394.png"
                  alt="Payment record form interface showing fields for amount, payment method, date, and reference details for recording invoice payments"
                  className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 mb-1">Payment Reference</h4>
                <p className="text-xs text-gray-600">
                  Use this form to record payments received against this invoice. 
                  All fields marked with * are required for accurate payment tracking.
                </p>
              </div>
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-2">Invoice Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Customer:</span>
                    <span className="text-blue-900 font-medium">
                      {invoice?.customer?.customer_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Total Amount:</span>
                    <span className="text-blue-900 font-medium">
                      ${totalAmount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Amount Paid:</span>
                    <span className="text-blue-900 font-medium">
                      ${amountPaid?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-1 mt-2">
                    <span className="text-blue-700 font-medium">Outstanding:</span>
                    <span className="text-blue-900 font-bold">
                      ${remainingBalance?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                  invoice?.status === 'paid' ? 'bg-green-100 text-green-800' :
                  invoice?.status === 'overdue' ? 'bg-red-100 text-red-800' :
                  invoice?.status === 'partially_paid'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {invoice?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Payment Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Amount Received *"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={remainingBalance}
                  value={formData?.amount_received || ''}
                  onChange={(e) => handleInputChange('amount_received', e?.target?.value)}
                  error={errors?.amount_received}
                  placeholder="0.00"
                  icon={DollarSign}
                />
              </div>

              <div>
                <Select
                  label="Payment Method *"
                  value={formData?.payment_method || ''}
                  onChange={(value) => handleInputChange('payment_method', value)}
                  options={[
                    { value: 'cash', label: 'Cash' },
                    { value: 'check', label: 'Check' },
                    { value: 'bank_transfer', label: 'Bank Transfer' },
                    { value: 'credit_card', label: 'Credit Card' },
                    { value: 'debit_card', label: 'Debit Card' },
                    { value: 'online', label: 'Online Payment' },
                    { value: 'upi', label: 'UPI' },
                    { value: 'wallet', label: 'Digital Wallet' }
                  ]}
                  error={errors?.payment_method}
                  icon={CreditCard}
                />
              </div>

              <div>
                <Input
                  label="Payment Date *"
                  type="date"
                  value={formData?.payment_date || ''}
                  onChange={(e) => handleInputChange('payment_date', e?.target?.value)}
                  error={errors?.payment_date}
                  icon={Calendar}
                />
              </div>

              <div>
                <Input
                  label="Reference Number"
                  value={formData?.reference_number || ''}
                  onChange={(e) => handleInputChange('reference_number', e?.target?.value)}
                  placeholder="Transaction/Check number"
                  icon={Receipt}
                />
              </div>

              {/* Conditional fields based on payment method */}
              {(formData?.payment_method === 'bank_transfer' || 
                formData?.payment_method === 'check') && (
                <>
                  <div>
                    <Input
                      label="Bank Name"
                      value={formData?.bank_name || ''}
                      onChange={(e) => handleInputChange('bank_name', e?.target?.value)}
                      placeholder="Bank name"
                    />
                  </div>
                  
                  <div>
                    <Input
                      label="Account Reference"
                      value={formData?.account_reference || ''}
                      onChange={(e) => handleInputChange('account_reference', e?.target?.value)}
                      placeholder="Account number or reference"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <Input
                  label="Description"
                  value={formData?.description || ''}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Payment description"
                />
              </div>

              <div className="md:col-span-2">
                <Select
                  label="Status"
                  value={formData?.status || 'pending'}
                  onChange={(value) => handleInputChange('status', value)}
                  options={[
                    { value: 'pending', label: 'Pending Clearance' },
                    { value: 'cleared', label: 'Cleared' },
                    { value: 'bounced', label: 'Bounced' }
                  ]}
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes || ''}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional notes about this payment"
              />
            </div>

            {/* Payment Summary */}
            {paymentAmount > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Amount:</span>
                    <span className="font-medium text-green-600">
                      +${paymentAmount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Outstanding:</span>
                    <span className="font-medium">
                      ${remainingBalance?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-gray-900 font-medium">New Balance:</span>
                    <span className={`font-bold ${newBalance <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                      ${Math.max(0, newBalance)?.toLocaleString()}
                    </span>
                  </div>
                  {newBalance <= 0 && (
                    <div className="flex items-center gap-2 text-green-600 pt-2">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Invoice will be marked as paid</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Error Display with customer_id specific handling */}
            {(errors?.submit || errors?.customer_id) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <p className="text-sm text-red-800">
                    {errors?.customer_id || errors?.submit}
                  </p>
                </div>
                {errors?.customer_id && (
                  <div className="mt-2">
                    <p className="text-xs text-red-600">
                      Suggestion: Edit this invoice to assign a customer, or contact support if this issue persists.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={loading || remainingBalance <= 0 || !invoice?.customer_id}
                className="bg-green-600 hover:bg-green-700"
              >
                Record Payment
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecordPaymentModal;